import React, { useEffect, useRef, useState } from "react";
import { getAuth, signOut } from "firebase/auth";
import { collection, doc, onSnapshot, writeBatch } from "firebase/firestore";
import { db, functions } from "./firebase";
import './AppList.css';
import { LineWave } from "react-loader-spinner";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Button,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Portal,
  useToast,
} from '@chakra-ui/react'
import { httpsCallable } from "firebase/functions";
import PortalHeader from './PortalHeader';
import AndroidVerification from './AndroidVerificationChecklist';

const AppList = () => {
  const navigate = useNavigate();
  const [apps, setApps] = useState([]);
  const [copiedLinks, setCopiedLinks] = useState({});
  const copyTimers = useRef({});

  useEffect(() => () => {
    Object.values(copyTimers.current).forEach(clearTimeout);
  }, []);
  const [movingApps, setMovingApps] = useState([]);
  const [registrationApps, setRegistrationApps] = useState([]);
  const [expandedApps, setExpandedApps] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showUnpublishedOnly, setShowUnpublishedOnly] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [publishLoading, setPublishLoading] = useState(true);
  const [publishAllOpen, setPublishAllOpen] = useState(false)
  const [publishOpen, setPublishOpen] = useState(null)
  const [isMember, setIsMember] = useState(false)
  const [previewNonMember, setPreviewNonMember] = useState(false)
  const allowPublish = isMember && !previewNonMember;
  const publishToast = useToast()
  const appsToPublish = apps.filter(app => app.stagingDownloadUrl);

  const fetchMembers = () => {
    const auth = getAuth();
    const membersDocRef = doc(db, "Settings", "Members");

    const unsubscribe = onSnapshot(membersDocRef, (document) => {
      try {
        const data = document.data();
        const email = auth.currentUser?.email;
        setIsMember(Boolean(email && Array.isArray(data?.testers) && data.testers.includes(email)));
      } catch (error) {
        setIsMember(false);
      }
    }, () => setIsMember(false));

    return unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = fetchMembers();
    return () => unsubscribe();
  }, []);

  const fetchData = () => {
    const appCollectionRef = collection(db, "App");

    const unsubscribe = onSnapshot(appCollectionRef, (querySnapshot) => {
      try {
        const appsMap = {};
        setRegistrationApps(querySnapshot.docs.map((snapshot) => ({ ...snapshot.data(), id: snapshot.id })));

        // Base (non-managed) docs
        querySnapshot.docs.forEach((doc) => {
          if (!doc.id.endsWith("-managed")) {
            appsMap[doc.id] = {
              id: doc.id,
              ...doc.data(),
              isProduction: doc.data().isProduction === true,
              isManaged: false,
              managed: {}, // placeholder for managed data
            };
          }
        });

        // Managed docs
        querySnapshot.docs.forEach((doc) => {
          if (doc.id.endsWith("-managed")) {
            const baseId = doc.id.replace(/-managed$/, "");

            if (appsMap[baseId]) {
              appsMap[baseId].managed = {
                id: doc.id,
                ...doc.data(),
                isManaged: true
              };
              appsMap[baseId].isManaged = true;
            }
          }
        });

        const appList = Object.values(appsMap).sort((a, b) =>
          a.appName.localeCompare(b.appName)
        );

        console.log(appList)

        setApps(appList);
      } catch (error) {
        setError("Unable to load release information: " + error.message);
      } finally {
        setLoading(false);
        setPublishLoading(false);
      }
    });

    return unsubscribe;
  };

  useEffect(() => {
    const unsubscribe = fetchData();
    return () => unsubscribe();
  }, []);

  const handleUpdateApp = async (app, updates, successMessage, errorAction) => {
    if (!allowPublish || movingApps.includes(app.id)) return;

    setMovingApps((previous) => [...previous, app.id]);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, "App", app.id), updates);
      if (app.managed?.id) {
        batch.update(doc(db, "App", app.managed.id), updates);
      }
      await batch.commit();
      publishToast({
        position: 'top',
        title: successMessage,
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      publishToast({
        position: 'top',
        title: `Unable to ${errorAction} ${app.appName}`,
        description: error.message,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setMovingApps((previous) => previous.filter((id) => id !== app.id));
    }
  };

  const handleMoveApp = (app) => handleUpdateApp(
    app,
    { isProduction: !app.isProduction },
    `${app.appName} moved to ${app.isProduction ? 'Internal' : 'Production'}`,
    'move'
  );

  const handleToggleDisabled = (app) => handleUpdateApp(
    app,
    { isDisabled: !app.isDisabled },
    `${app.appName} ${app.isDisabled ? 'enabled' : 'disabled'}`,
    app.isDisabled ? 'enable' : 'disable'
  );

  const handleDownload = (downloadUrl) => {
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.click();
  };

  const renameFileAndUpdateDocFn = httpsCallable(functions, 'renameFileAndUpdateDocAfterPublishing');
  const getPublishAppName = (app) => app.publish?.appName || app.appName;

  const handlePublish = async (app, allMode) => {
    if (!allMode) {
      setPublishLoading(true)
      setPublishOpen(null)
    }

    try {
      const result = await renameFileAndUpdateDocFn({
        id: app.id
      });

      console.log(result.data);

      publishToast({
        position: 'top',
        title: `${getPublishAppName(app)} published`,
        status: 'success',
        isClosable: true,
      })
    } catch (error) {
      console.error(`Error publishing ${getPublishAppName(app)}: `, error.message);
      publishToast({
        position: 'top',
        title: `Unable to publish ${getPublishAppName(app)}`,
        description: error.message,
        status: 'error',
        isClosable: true,
      })
    }

    if (!allMode) {
      setPublishLoading(false)
    }
  };

  const handlePublishAll = async () => {
    setPublishAllOpen(true)
  };

  const onPublishAppPressed = async (app) => {
    setPublishOpen(app)
  }

  const confirmPublishAll = async () => {
    setPublishLoading(true)
    setPublishAllOpen(false)
    for (const app of appsToPublish) {
      await handlePublish(app, true);
    }

    setPublishLoading(false)

    // publishToast({
    //   position: 'top',
    //   title: `Successfully published all the apps`,
    //   status: 'success',
    //   isClosable: true,
    // })
  };

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth).then(() => {
      window.location.href = "/login";
    }).catch((error) => {
      console.error("Error signing out: ", error);
    });
  };

  const toggleExpand = (appId) => {
    setExpandedApps((prev) =>
      prev.includes(appId)
        ? prev.filter((id) => id !== appId)  // <-- FIXED
        : [...prev, appId]
    );
  };

  function PublishAllConfirmationAlert() {
    const cancelRef = React.useRef()

    return (
      <>
        <AlertDialog
          isOpen={publishAllOpen}
          leastDestructiveRef={cancelRef}
          onClose={() => setPublishAllOpen(false)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                Publish all
              </AlertDialogHeader>

              <AlertDialogBody>
                Publish all available staging releases? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setPublishAllOpen(false)}>
                  Cancel
                </Button>
                <Button colorScheme='green' onClick={() => confirmPublishAll()} ml={3}>
                  Publish all
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </>
    )
  }

  function PublishConfirmationAlert() {
    const cancelRef = React.useRef()

    if (publishOpen == null) return

    return (
      <>
        <AlertDialog
          isOpen={true}
          leastDestructiveRef={cancelRef}
          onClose={() => setPublishOpen(null)}
        >
          <AlertDialogOverlay>
            <AlertDialogContent>
              <AlertDialogHeader fontSize='lg' fontWeight='bold'>
                Publish {getPublishAppName(publishOpen)}
                {publishOpen.isManaged && " (Managed)"}
              </AlertDialogHeader>

              <AlertDialogBody>
                Publish the staging release of {getPublishAppName(publishOpen)}
                {publishOpen.isManaged && " (Managed)"}? This action cannot be undone.
              </AlertDialogBody>

              <AlertDialogFooter>
                <Button ref={cancelRef} onClick={() => setPublishOpen(null)}>
                  Cancel
                </Button>
                <Button colorScheme='green' onClick={() => handlePublish(publishOpen, false)} ml={3}>
                  Publish
                </Button>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialogOverlay>
        </AlertDialog>
      </>
    )
  }

  const openInNewTab = (url) => {
    const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
    if (newWindow) newWindow.opener = null;
  };

  const renderDownloadPageActions = (refId, appName) => {
    if (!refId) return null;
    const url = `https://fieldops.abadata.ca/app?id=${encodeURIComponent(refId)}`;
    const copyKey = `${refId}:${appName}`;
    const copyLink = async () => {
      try {
        await navigator.clipboard.writeText(url);
        setCopiedLinks((previous) => ({ ...previous, [copyKey]: true }));
        clearTimeout(copyTimers.current[copyKey]);
        copyTimers.current[copyKey] = setTimeout(() => {
          setCopiedLinks((previous) => {
            const next = { ...previous };
            delete next[copyKey];
            return next;
          });
          delete copyTimers.current[copyKey];
        }, 2000);
        publishToast({ title: `${appName} download page link copied`, status: 'success', position: 'top', isClosable: true });
      } catch (error) {
        publishToast({ title: `Unable to copy link for ${appName}`, description: 'Please open the download page and copy its address.', status: 'error', position: 'top', isClosable: true });
      }
    };
    return (
      <div className="download-page-actions">
        <button onClick={() => openInNewTab(url)} className="download-page-button">Open download page</button>
        <button onClick={copyLink} className="copy-page-link-button" aria-label={`Copy download page link for ${appName}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="8" y="8" width="12" height="12" rx="2" /><path d="M16 8V4H4v12h4" /></svg>
          {copiedLinks[copyKey] ? 'Copied' : 'Copy link'}
        </button>
      </div>
    );
  };

  const getPlayStoreInfo = (app) => {
    if (!app?.playstore) return null;

    return {
      internalUrl: app.playstore.internalUrl,
      prodUrl: app.playstore.prodUrl ?? app.playstore.prodUr,
      storeUrl: app.playstore.storeUrl,
    };
  };

  const hasPlayStoreInfo = (app) => Boolean(app?.playstore);

  const hasUnpublishedVersion = (app) => Boolean(app?.stagingDownloadUrl);
  const hasManagedContent = (app) => Boolean(app?.managed?.id);

  const renderStagingBadge = (app, titlePrefix = "") => {
    if (!allowPublish || !hasUnpublishedVersion(app)) return null;

    return (
      <span className="staging-badge">
        {titlePrefix}Staging available
        {app.stagingVersionName && ` ${app.stagingVersionName}`}
        {app.stagingVersionCode != null && ` (${app.stagingVersionCode})`}
      </span>
    );
  };

  const renderPlayStoreLinks = (app, titlePrefix = "") => {
    const playStoreInfo = getPlayStoreInfo(app);

    if (!playStoreInfo) return null;

    return (
      <>
        {playStoreInfo.storeUrl && (
          <div className="version-item">
            <label style={{ fontWeight: 'bold' }}>{titlePrefix}Store listing</label>
            <small>Open the public Play Store listing.</small>
            <button
              onClick={() => openInNewTab(playStoreInfo.storeUrl)}
              className="download-page-button"
            >
              Open store listing
            </button>
          </div>
        )}

        {playStoreInfo.prodUrl && (
          <div className="version-item">
            <label style={{ fontWeight: 'bold' }}>{titlePrefix}Published release</label>
            <small>This app is published through the Play Store.</small>
            <button
              onClick={() => openInNewTab(playStoreInfo.prodUrl)}
              className="download-button"
            >
              View published release
            </button>
          </div>
        )}

        {allowPublish && playStoreInfo.internalUrl && (
          <div className="version-item">
            <label style={{ fontWeight: 'bold' }}>{titlePrefix}Internal test release</label>
            <small>View the internal testing track in Play Console.</small>
            <button
              onClick={() => openInNewTab(playStoreInfo.internalUrl)}
              className="download-button"
            >
              View internal test release
            </button>
          </div>
        )}
      </>
    );
  };

  const formatDate = (date) => {
    const options = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    };
    return new Intl.DateTimeFormat('en-US', options).format(date.toDate());
  };

  const normalizedSearchTerm = searchTerm.trim().toLowerCase();
  const getAppExpandKey = (app) => app.id ?? app.appId;

  const visibleApps = allowPublish ? apps : apps.filter((app) => app.isProduction && app.isDisabled !== true);
  const filteredApps = visibleApps.filter((app) => {
    const appNames = [app.appName, app.secondAppName]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    const matchesSearch = normalizedSearchTerm.length === 0 || appNames.includes(normalizedSearchTerm);
    const appHasUnpublishedVersion = Boolean(hasUnpublishedVersion(app) || hasUnpublishedVersion(app.managed));

    return matchesSearch && (!allowPublish || !showUnpublishedOnly || appHasUnpublishedVersion);
  });

  const sortedApps = [...filteredApps].sort((a, b) => {
    const nameA = (a.appName ?? "").toLowerCase();
    const nameB = (b.appName ?? "").toLowerCase();

    return nameA.localeCompare(nameB, undefined, { numeric: true, sensitivity: "base" });
  });

  return (
    <div className="AppList">
      <PortalHeader>
          {isMember && <button
            onClick={() => setPreviewNonMember((previous) => !previous)}
            className="member-preview-button"
            aria-pressed={previewNonMember}
          >
            {previewNonMember ? 'Back to developer view' : 'Preview non-developer view'}
          </button>}
          {allowPublish && <button
            disabled={appsToPublish.length === 0 || !allowPublish || publishLoading}
            onClick={handlePublishAll}
            className="publish-all-button"
            data-tip={allowPublish ? 'Publish all available staging releases' : 'Publishing permission is required'}
          >
            Publish all
          </button>}
          {allowPublish && <button onClick={() => navigate("/updates")} className="update-dashboard-button">
            App updates
          </button>}
          <button onClick={handleLogout} className="logout-button">Sign out</button>
      </PortalHeader>
      <main className="main-content">
        <div className="release-page-heading">
          <div>
            <p className="release-page-eyebrow">Release management</p>
            <h1>App releases</h1>
            <p className="release-page-description">{allowPublish ? 'Review published versions and prepare staging releases for distribution.' : 'View live production app releases.'}</p>
          </div>
          {!loading && !error && <span className="release-app-count">{visibleApps.length} {visibleApps.length === 1 ? 'app' : 'apps'}</span>}
        </div>
        {loading && (
          <div className="loading">
            <LineWave
              visible={true}
              height="100"
              width="100"
              color="#FE8100"
              ariaLabel="Loading app releases"
            />
          </div>
        )}
        {error && <p className="error">{error}</p>}
        {!loading && !error && (
          <>
            {allowPublish && <div style={{ marginBottom: 16 }}>
              <AndroidVerification apps={registrationApps} canEdit={allowPublish} />
            </div>}
            <div className="list-controls">
              <div className="list-control search-control">
                <label htmlFor="app-search">Search by name</label>
                <input
                  id="app-search"
                  type="search"
                  placeholder="Search apps"
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                />
              </div>

              {allowPublish && <label className="toggle-filter">
                <input
                  type="checkbox"
                  checked={showUnpublishedOnly}
                  onChange={(event) => setShowUnpublishedOnly(event.target.checked)}
                />
                <span>Show apps with staging releases</span>
              </label>}
            </div>

            {sortedApps.length === 0 && (
              <p className="empty-state">No apps match the current search and filters.</p>
            )}

            {[
              { title: 'Production Apps', isProduction: true },
              { title: 'Internal Apps', isProduction: false },
            ].filter((section) => allowPublish || section.isProduction).map((section) => {
              const sectionApps = sortedApps.filter((app) => app.isProduction === section.isProduction);
              return (
              <section key={section.title} className="app-section" aria-label={section.title}>
                <div className="app-section-heading">
                  <h2>{section.title}</h2>
                  <span>{sectionApps.length} {sectionApps.length === 1 ? 'app' : 'apps'}</span>
                </div>
                {sectionApps.length === 0 && <p className="empty-state">No {section.isProduction ? 'production' : 'internal'} apps match the current search and filters.</p>}
            <div className="app-cards">
              {sectionApps.map((app) => (
                <div key={app.id} className={`app-card ${app.isDisabled === true ? 'is-disabled ' : ''}${expandedApps.includes(getAppExpandKey(app)) ? 'expanded' : ''}${allowPublish && (hasUnpublishedVersion(app) || hasUnpublishedVersion(app.managed)) ? ' has-staging' : ''}`}>
                  <div className="app-header">
                    <img src={app.appIconUrl} alt={`${app.appName} logo`} className="app-icon" />
                    <div className="app-title-group">
                      <h2 className="app-name">
                        {app.appName}
                        {app.secondAppName && ` (${app.secondAppName})`}
                      </h2>
                      {hasPlayStoreInfo(app) ? (
                        <p className="app-version-summary">
                          Published through the Play Store
                        </p>
                      ) : app.versionName && app.versionCode && (
                        <p className="app-version-summary">
                          Published version {app.versionName} ({app.versionCode})
                        </p>
                      )}
                      <div className="release-badges">
                        {app.isDisabled === true && <span className="disabled-badge">Disabled</span>}
                        {app.isManaged && <span className="managed-badge">Managed</span>}
                        {renderStagingBadge(app)}
                        {renderStagingBadge(app.managed, "Managed · ")}
                      </div>
                    </div>
                    <div className="app-card-actions">
                      {allowPublish && (
                        <Menu placement="bottom-end">
                          <MenuButton
                            as={IconButton}
                            aria-label={`Actions for ${app.appName}`}
                            title="App actions"
                            variant="ghost"
                            color="gray.400"
                            _hover={{ color: 'gray.600', bg: 'gray.50' }}
                            _active={{ color: 'gray.600', bg: 'gray.100' }}
                            size="sm"
                            isDisabled={movingApps.includes(app.id)}
                            isLoading={movingApps.includes(app.id)}
                            icon={<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="19" cy="12" r="2" /></svg>}
                          />
                          <Portal>
                            <MenuList minWidth="180px" fontSize="sm">
                              <MenuItem
                                onClick={() => handleMoveApp(app)}
                                aria-label={`Move ${app.appName} to ${app.isProduction ? 'Internal' : 'Production'}`}
                              >
                                Move to {app.isProduction ? 'Internal' : 'Production'}
                              </MenuItem>
                              <MenuItem
                                onClick={() => handleToggleDisabled(app)}
                                aria-label={`${app.isDisabled ? 'Enable' : 'Disable'} ${app.appName}`}
                              >
                                {app.isDisabled ? 'Enable app' : 'Disable app'}
                              </MenuItem>
                            </MenuList>
                          </Portal>
                        </Menu>
                      )}
                    <button
                      onClick={() => toggleExpand(getAppExpandKey(app))}
                      className="expand-button"
                      aria-label={`${expandedApps.includes(getAppExpandKey(app)) ? 'Hide' : 'Show'} release details for ${app.appName}`}
                      aria-expanded={expandedApps.includes(getAppExpandKey(app))}
                    >
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
                    </button>
                    </div>
                  </div>
                  <div className={`app-details ${expandedApps.includes(getAppExpandKey(app)) ? 'expanded' : ''}`}>
                    {hasPlayStoreInfo(app) ? (
                      renderPlayStoreLinks(app)
                    ) : (
                      <>
                        {app.downloadUrl && (
                          <div className="version-item">
                            <label style={{ fontWeight: 'bold' }}>Published version {app.versionName} ({app.versionCode})</label>
                            <small>Published by {app.publisher}</small>
                            {
                              app.updatedTime ? <small>Published on {formatDate(app.updatedTime)}</small> : <></>
                            }
                            {renderDownloadPageActions(app.refId, app.appName)}
                          </div>
                        )}

                        {allowPublish && app.stagingDownloadUrl && (
                          <div className="version-item">
                            <label style={{ fontWeight: 'bold' }}>Staging version {app.stagingVersionName} ({app.stagingVersionCode})</label>
                            {
                              app.stagingUpdatedTime ? <small>Updated on {formatDate(app.stagingUpdatedTime)}</small> : <></>
                            }
                            <button onClick={() => handleDownload(app.stagingDownloadUrl)} className="download-button">Download</button>
                            <button
                              disabled={!allowPublish || publishOpen || publishLoading}
                              style={{ marginTop: 5 }}
                              onClick={() => onPublishAppPressed(app)}
                              className="publish-button"
                            >
                              Publish
                            </button>
                          </div>
                        )}

                        {allowPublish && app.previousVersionName && app.previousVersionCode && (
                          <div className="version-item">
                            <label style={{ fontWeight: 'bold' }}>Previous version {app.previousVersionName} ({app.previousVersionCode})</label>
                            {
                              app.previousUpdatedTime ? <small>Published on {formatDate(app.previousUpdatedTime)}</small> : <></>
                            }
                          </div>
                        )}

                      </>
                    )}

                    {hasManagedContent(app) && (allowPublish || app.managed.isDisabled !== true) && <>
                    <div className="divider"></div>
                    {!allowPublish ? (
                      <div className="managed-version-summary">
                        <span className="managed-version-label">Managed build</span>
                        <strong>{app.managed.versionName ? `Version ${app.managed.versionName}${app.managed.versionCode != null ? ` (${app.managed.versionCode})` : ''}` : 'Version not available'}</strong>
                      </div>
                    ) : hasPlayStoreInfo(app.managed) ? (
                      renderPlayStoreLinks(app.managed, "Managed · ")
                    ) : (
                      <>
                        {app.managed?.downloadUrl && (
                          <>
                            <div className="version-item">
                              <label style={{ fontWeight: 'bold' }}>
                                Managed · Published version {app.managed.versionName} ({app.managed.versionCode})
                              </label>
                              <small>Published by {app.managed.publisher}</small>
                              {app.managed.updatedTime ? (
                                <small>Published on {formatDate(app.managed.updatedTime)}</small>
                              ) : (
                                <></>
                              )}
                              {renderDownloadPageActions(app.managed.refId || app.refId, `${app.appName} (Managed)`)}
                            </div>
                          </>
                        )}

                        {allowPublish && app.managed?.stagingDownloadUrl && (
                          <div className="version-item">
                            <label style={{ fontWeight: 'bold' }}>
                              Managed · Staging version {app.managed.stagingVersionName} ({app.managed.stagingVersionCode})
                            </label>

                            {app.managed.stagingUpdatedTime ? (
                              <small>Updated on {formatDate(app.managed.stagingUpdatedTime)}</small>
                            ) : (
                              <></>
                            )}

                            <button
                              onClick={() => handleDownload(app.managed.stagingDownloadUrl)}
                              className="download-button"
                            >
                              Download
                            </button>

                            <button
                              disabled={!allowPublish || publishOpen || publishLoading}
                              style={{ marginTop: 5 }}
                              onClick={() => onPublishAppPressed(app.managed, { managed: true })}
                              className="publish-button"
                            >
                              Publish
                            </button>
                          </div>
                        )}

                        {allowPublish && app.managed?.previousVersionName && app.managed.previousVersionCode && (
                          <div className="version-item">
                            <label style={{ fontWeight: 'bold' }}>
                              Managed · Previous version {app.managed.previousVersionName} ({app.managed.previousVersionCode})
                            </label>
                            {
                              app.managed.previousUpdatedTime ? <small>Published on {formatDate(app.managed.previousUpdatedTime)}</small> : <></>
                            }
                          </div>
                        )}

                      </>
                    )}
                    </>}
                  </div>
                </div>
              ))}
            </div>
              </section>
              );
            })}
          </>
        )
        }
      </main >
      {allowPublish && <PublishConfirmationAlert />}
      {allowPublish && <PublishAllConfirmationAlert />}
    </div >
  );
};

export default AppList;
